import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import ItemHeader from "../ItemHeader/ItemHeader.web";
import ItemVisibility from "../ItemVisibility/ItemVisibility.web";
import { CartPendingItemProps } from "./CartPendingItem.props";

/**
 * Displays information about seating items that are selected but are "pending" being added to the cart.
 * Example: Seats with minimum purchase limits, before the required number of seats has been selected.
 */
const CartPendingItem = ({
  seatItem,
  isMultiItemLimit = false,
}: CartPendingItemProps) => {
  const { t } = useTranslation("seating");
  const { removeSeatPermission, getItemTypeInPendingCart } =
    useAssignedSeatingHelpers();
  const { seatingEventData } = useSeatingState();

  const headerVals = useMemo(
    () => [
      {
        id: 0,
        title: t("Section"),
        value: seatItem.$data.segment_identifier,
      },
      {
        id: 1,
        title: t("Row"),
        value: seatItem.$data.location_identifier,
      },
      {
        id: 2,
        title: t("Seat"),
        value: seatItem.$data.identifier,
      },
    ],
    [t, seatItem]
  );

  return (
    <Box
      m="0px 20px"
      p="xs"
      borderRadius="md"
      backgroundColor="gray.200"
      mb="md"
      key={seatItem.$data.id}
    >
      {/* Hide text when multiple item types can be added to make up the purchase limit */}
      {!isMultiItemLimit && (
        <Text fontSize="xs" mb="xs">{`*${t("Minimum {{count}} items required", {
          count: seatItem?.minPurchaseLimit,
        })}`}</Text>
      )}

      <ItemHeader
        headerVals={headerVals}
        removeItem={() =>
          removeSeatPermission(
            seatItem?.permissionId,
            seatItem?.itemGroupParent
          )
        }
        disableRemoveButton={false}
      />

      <Divider my="xs" />

      <Text mb="2xs" fontSize="sm">{`${getItemTypeInPendingCart()?.name} - ${
        seatingEventData?.name
      }`}</Text>

      {/* Accessibility / Limited Visibility Text for seats with minimum purchase limits */}
      <ItemVisibility
        isLimitedVisibility={seatItem.$data.is_limited_visibility}
        isAccessible={seatItem.$data.is_accessible}
      />
    </Box>
  );
};

export default CartPendingItem;
